import { useEffect, useState } from "react";
import { KitModel } from "../../Api/Models/product.model";
import { kitService } from "../../Api/Services/KitService";
import { RiderModel } from "../../Api/Models/rider.model";
import { useLoading } from "../../Context/LoadingProvider";
import { useInfoModal } from "../../Context/InfoModalProvider";
import { useTranslation } from "react-i18next";
import ProductWithSizes from "./Assign/ProductWithSizes";
import ProductGroup from "./Assign/ProductGroup";
import RemainingProducts from "./Assign/RemainingProducts";
import ConfrimationScreen from "./Assign/ConfirmationScreen";
import { riderService } from "../../Api/Services/RiderService";
import { LaravelValidationError } from "../../Api/request";
import { useOutletContext } from "react-router-dom";
import { RiderProfileLayoutContextInterface } from "./RiderProfileLayout";

interface AssignGearProps {
  rider: RiderModel;
  finnishAssign: CallableFunction;
  missingProductsKit?: KitModel | null;
  skipedAllProducts?: CallableFunction;
}

type BackAction =
    | { type: "single"; id: number }
    | { type: "group"; groupId: number }
    | { type: "batch"; ids: number[] };

const AssignGear: React.FunctionComponent<AssignGearProps> = ({
  rider,
  finnishAssign: updateGears,
  missingProductsKit,
  skipedAllProducts,
}) => {
  const [kitToAssign, setKitToAssign] = useState<KitModel | null>(
    missingProductsKit ?? null
  );
  const { startLoading, finnishLoading } = useLoading();
  const { showInfo } = useInfoModal();
  const { t } = useTranslation();
  const { fetchRider } =
    useOutletContext<RiderProfileLayoutContextInterface>();

  useEffect(() => {
    if (rider.vehicle && !missingProductsKit) {
      startLoading("get-kit-to-assign");
      kitService
        .getKitForRider(rider.vehicle, rider.riderId)
        .then((kit) => {
          setKitToAssign(kit);

          if (!kit) {
            showInfo(t("No kit available for this rider type!"));
          }
        })
        .finally(() => {
          finnishLoading("get-kit-to-assign");
        });
    }
    //eslint-disable-next-line
  }, []);

  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = kitToAssign?.products.filter(product => product.stockEnough).length ?? 0;
  const [history, setHistory] = useState<BackAction[]>([]);

  const toggleItemChecked = (productId: number) => {
    if (!kitToAssign?.products) return;

    const current = kitToAssign.products.find(p => p.id === productId);
    if (!current) return;

    const nextChecked = !current.checked;

    const next = kitToAssign.products.map((p) =>
        p.id === productId
            ? {
              ...p,
              checked: nextChecked,
              selected: nextChecked,
              skipped: false,
              prepared: false,
            }
            : p
    );

    setKitToAssign({ ...kitToAssign, products: next });

    if (nextChecked) {
      setHistory((h) => [...h, { type: "single", id: productId }]);
      setCurrentStep((prev) => Math.min(prev + 1, totalSteps));
    } else {
    }
  };


  const setKitProductVariation = (kitProductId: number, selectedVariationId: number) => {
    if (!kitToAssign?.products) return;

    const kitToAssignProductsState = kitToAssign.products.map((p) =>
        p.id === kitProductId
            ? {
              ...p,
              selectedVariationId,
              checked: true,
              selected: true,
              skipped: false,
            }
            : p
    );

    setKitToAssign({ ...kitToAssign, products: kitToAssignProductsState });
    setCurrentStep((prevStep) => Math.min(prevStep + 1, totalSteps));
    setHistory((h) => [...h, { type: "single", id: kitProductId }]);
  };

  const setKitProductGroupChoice = (
      groupId: number,
      kitProductId: number,
      selectedVariationId: number | null
  ) => {
    if (!kitToAssign?.products) return;

    const chosen = kitToAssign.products.find((p) => p.id === kitProductId);
    if (!chosen) return;

    const chosenRequiresVar = (chosen.variations?.length ?? 0) > 0;

    if (chosenRequiresVar && !selectedVariationId) {
      showInfo(t("Please select a size first."));
      return;
    }

    const kitToAssignProductsState = kitToAssign.products.map((p) => {
      if (p.optionalGroupId?.toString() === groupId.toString()) {
        return {
          ...p,
          checked: true,
          selected: p.id === kitProductId,
          skipped: false,
          selectedVariationId: p.id === kitProductId ? (selectedVariationId ?? null) : null,
        };
      }
      return p;
    });

    setKitToAssign({ ...kitToAssign, products: kitToAssignProductsState });
    setCurrentStep((prevStep) => Math.min(prevStep + 1, totalSteps));
    setHistory((h) => [...h, { type: "group", groupId }]);
  };

  /*const setItemChecked = (productId: number) => {
    if (!kitToAssign?.products) return;

    const kitToAssignProductsState = kitToAssign.products.map((p) =>
        p.id === productId
            ? { ...p, checked: true, selected: true, skipped: false }
            : p
    );

    setKitToAssign({ ...kitToAssign, products: kitToAssignProductsState });
  };*/

  const setItemPrepared = (productId: number) => {
    if (!kitToAssign?.products) return;

    const kitToAssignProductsState = kitToAssign.products.map((product) => {
      if (product.id !== productId) return product;

      const requiresVariation = (product.variations?.length ?? 0) > 0;

      if (requiresVariation && !product.selectedVariationId) {
        showInfo(t("Please select a size first."));
        return product;
      }

      return { ...product, prepared: !product.prepared };
    });

    setKitToAssign({ ...kitToAssign, products: kitToAssignProductsState });
  };

  const uncheckProduct = (productId: number) => {
    if (!kitToAssign?.products) return;

    const target = kitToAssign.products.find((x) => x.id === productId);
    if (!target) return;

    const groupId = target.optionalGroupId ?? -1;

    const kitToAssignProductsState = kitToAssign.products.map((p) => {
      const sameGroup = p.optionalGroupId && p.optionalGroupId === groupId;

      if (p.id === productId || sameGroup) {
        return {
          ...p,
          checked: false,
          selected: false,
          skipped: false,
          prepared: false,
          selectedVariationId: null,
        };
      }
      return p;
    });

    setKitToAssign({ ...kitToAssign, products: kitToAssignProductsState });
  };

  const skipProduct = (productId: number) => {
    if (!kitToAssign?.products) return;

    const target = kitToAssign.products.find((x) => x.id === productId);
    if (!target) return;

    const groupId = target.optionalGroupId ?? -1;

    const kitToAssignProductsState = kitToAssign.products.map((p) => {
      const sameGroup = p.optionalGroupId && p.optionalGroupId === groupId;

      if (p.id === productId || sameGroup) {
        return {
          ...p,
          checked: true,
          skipped: true,
          selected: false,
          prepared: false,
          selectedVariationId: null,
        };
      }
      return p;
    });
    setHistory((h) => {
      if (groupId !== -1) {
        return [...h, { type: "group", groupId }];
      }
      return [...h, { type: "single", id: productId }];
    });
    setCurrentStep((prevStep) => Math.min(prevStep + 1, totalSteps));

    if (
        !kitToAssignProductsState.some((x) => !x.skipped && x.stockEnough) &&
        skipedAllProducts
    ) {
      const reset = kitToAssignProductsState.map((p) => ({
        ...p,
        checked: false,
        skipped: false,
        selected: false,
        prepared: false,
        selectedVariationId: null,
      }));
      setKitToAssign({ ...kitToAssign, products: reset });
      skipedAllProducts();
      setCurrentStep(totalSteps);
      return;
    }

    setKitToAssign({ ...kitToAssign, products: kitToAssignProductsState });
  };

  const skipRemainingProducts = () => {
    if (!kitToAssign?.products) return;

    const remainingIds = kitToAssign.products
        .filter(
            (x) =>
                !(x.variations.length > 0 || x.optionalGroupId) &&
                !x.checked &&
                x.stockEnough &&
                !x.skipped
        )
        .map((x) => x.id);

    setCurrentStep(totalSteps);

    if (remainingIds.length === 0) return;

    const nextProducts = kitToAssign.products.map((p) =>
        remainingIds.includes(p.id)
            ? {
              ...p,
              checked: true,
              skipped: true,
              selected: false,
              prepared: false,
              selectedVariationId: null,
            }
            : p
    );

    setKitToAssign({ ...kitToAssign, products: nextProducts });
    setHistory((h) => [...h, { type: "batch", ids: remainingIds }]);
  };

  const back = () => {
    setHistory((h) => {
      const last = h[h.length - 1];
      if (!last) return h;

      setKitToAssign((prevKit) => {
        if (!prevKit?.products) return prevKit;

        const nextProducts = prevKit.products.map((p) => {
          const match =
              last.type === "single"
                  ? p.id === last.id
                  : last.type === "group"
                      ? p.optionalGroupId === last.groupId
                      : last.ids.includes(p.id);

          if (!match) return p;

          return {
            ...p,
            checked: false,
            selected: false,
            skipped: false,
            prepared: false,
            selectedVariationId: null,
          };
        });

        return { ...prevKit, products: nextProducts };
      });

      setCurrentStep((prev) => Math.max(prev - 1, 1));
      return h.slice(0, -1);
    });
  };

  const skipAll = () => {
    if (!kitToAssign?.products) return;

    const productsState = kitToAssign.products.map((p) => ({
      ...p,
      skipped: true,
      checked: true,
      selected: false,
      prepared: false,
      selectedVariationId: null,
    }));

    setKitToAssign({ ...kitToAssign, products: productsState });
    setCurrentStep(totalSteps);
  };


  const assignKit = async(images: File[], boxKeys: { [productId: number]: string }) => {
    if (!kitToAssign) {
      return;
    }

    console.log(history);

    startLoading("assign-kit");

    const productsToAssign = kitToAssign.products.filter((x) => x.prepared);

    const invalid = productsToAssign.filter(
        (x) => (x.variations?.length ?? 0) > 0 && !x.selectedVariationId
    );

    if (invalid.length > 0) {
      const details = invalid
          .map((x) =>
              `kitProductId=${x.id}, productId=${x.productId}, optionalGroupId=${x.optionalGroupId ?? "-"}, variations=${x.variations?.length ?? 0}`
          )
          .join("\n");

      showInfo(
          `${t("Some items require a size. Please select it before continuing.")}\n\n${details}`
      );

      console.warn("Missing size for items:", invalid);
      finnishLoading("assign-kit");
      return;
    }

    try {
      await riderService.assignKit(
          rider.riderId,
          kitToAssign.id,
          productsToAssign,
          images,
          boxKeys
      );

      updateGears();
      fetchRider();
    } catch (error: unknown) {
      if (
          typeof error === "object" &&
          error !== null &&
          "errors" in error
      ) {
        const laravelError = error as LaravelValidationError;

        if (laravelError.errors?.errorMessage) {
          showInfo(laravelError.errors.errorMessage);
        }
      }
    } finally {
      finnishLoading("assign-kit");
    }
  };

  if (!kitToAssign || !kitToAssign.products) {
    return null;
  }

  let notCheckedProducts = kitToAssign.products.filter(
    (x) => !x.checked && x.stockEnough && !x.skipped
  );
  if (notCheckedProducts.length === 0 && currentStep === totalSteps) {
    return (
      <ConfrimationScreen
        kitProducts={kitToAssign.products}
        uncheckProduct={uncheckProduct}
        setItemPrepared={setItemPrepared}
        assignKit={assignKit}
        back={back}
        currentStep={currentStep}
        totalSteps={totalSteps}
      />
    );
  }

  let variationalProducts = notCheckedProducts.filter(
    (x) => x.variations.length > 0 && !x.optionalGroupId
  );
  if (variationalProducts.length > 0) {
    return (
      <ProductWithSizes
        kitProduct={variationalProducts[0]}
        setKitProductVariation={setKitProductVariation}
        skipProduct={skipProduct}
        skipAll={skipAll}
        back={kitToAssign.products.some((x) => x.checked) ? back : null}
        currentStep={currentStep}
        totalSteps={totalSteps}
        kitToAssignProducts={kitToAssign.products}
      />
    );
  }

  let groupedProducts = notCheckedProducts.filter((x) => x.optionalGroupId);
  if (groupedProducts.length > 0) {
    return (
      <ProductGroup
        kitProducts={groupedProducts.filter(
          (x) => x.optionalGroupId === groupedProducts[0].optionalGroupId
        )}
        setKitProductGroupChoice={setKitProductGroupChoice}
        skipProduct={skipProduct}
        skipAll={skipAll}
        back={kitToAssign.products.some((x) => x.checked) ? back : null}
        currentStep={currentStep}
        totalSteps={totalSteps}
      />
    );
  }

  let remainingProducts = kitToAssign.products.filter(
    (x) => !(x.variations.length > 0 || x.optionalGroupId)
  );
    return (
        <RemainingProducts
            kitProducts={remainingProducts}
            toggleItemChecked={toggleItemChecked}
            skipRemainingProducts={skipRemainingProducts}
            back={back}
            currentStep={currentStep}
            totalSteps={totalSteps}
        />
    );
};

export default AssignGear;
